import clsx from "clsx";
import { type FC, type ReactNode, useCallback, useEffect } from "react";
import { useBoolean } from "usehooks-ts";

import { userHasGlobalAuthority } from "../../../../../shared/capabilities";
import {
  CourseRole,
  MaxRole,
  OrgRole,
  SectionRole,
  UserRole,
} from "../../../../../shared/types/roles";
import { AddUserModal } from "../AddUserModal";
import { BulkImportMenu } from "../bulk-import/BulkImportMenu";

import { AdminUserOptionsMenu } from "./AdminUserOptionsMenu";
import { OrgRoleIconWithTooltip, getDisplayOrgRole } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { EditOrgUserModal } from "components/modals/EditOrgUserModal";
import type {
  BulkImportType,
  CourseGroupingUserType,
  CourseUserType,
  OrgUserType,
  SectionUserType,
  UserType,
} from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TR } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { ArrowDownToLineIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  courseGroupingUserSelectors,
  courseUserSelectors,
  orgUserSelectors,
  sectionSelectors,
  selectCurrentOrg,
  selectEnabledSectionUsersByRole,
  selectIsEditModeEnabled,
  userSelectors,
} from "store/selectors";
import { selectUserInviteStatuses } from "store/selectors/computed";
import { sortByFirstName } from "store/selectors/sorting";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateOrgUsersCSV } from "utils/reporting";
import { getSectionResourceRole } from "utils/section";
import { useFetchUserInviteStatuses } from "utils/user-invite-status";
import { getUserFullName } from "utils/user-utils";

type AdminUserEntry = {
  user: UserType;
  orgUser: Pick<OrgUserType, "id" | "external_user_id" | "role" | "is_disabled">;
  tooltipSectionRoleTable: ReactNode;
  inviteStatus: InviteStatus;
  maxRole: MaxRole;
  isCourseAdmin: boolean;
};

const ORG_ROLE_SORT_ORDER: Record<OrgRole | CourseRole, number> = {
  admin: 0,
  course_admin: 1,
  recording_agent: 3, // filtered out above; here for exhaustiveness
};

const getOrgRoleSortOrder = (role: OrgRole | CourseRole | null): number =>
  role == null ? 2 : ORG_ROLE_SORT_ORDER[role];

const MAX_ROLE_SORT_ORDER: Record<MaxRole, number> = {
  super_admin: 0,
  global_admin: 1,
  org_admin: 2,
  course_admin: 3,
  instructor: 4,
  section_assistant: 5,
  student: 6,
};

function getMaxRole(
  orgUser: Pick<OrgUserType, "role">,
  user: UserType,
  sectionUsersForUser: Pick<SectionUserType, "role">[],
  courseUsersForUser: Pick<CourseUserType, "role">[],
  courseGroupingUsers: Pick<CourseGroupingUserType, "role">[],
): MaxRole {
  if (user.role === UserRole.SUPER_ADMIN) return MaxRole.SUPER_ADMIN;
  if (user.role === UserRole.GLOBAL_ADMIN) return MaxRole.GLOBAL_ADMIN;

  if (orgUser.role === OrgRole.ADMIN) return MaxRole.ORG_ADMIN;

  const hasCourseAdminForGrouping = courseGroupingUsers.some(
    (cu) => cu.role === CourseRole.COURSE_ADMIN,
  );
  if (hasCourseAdminForGrouping) return MaxRole.COURSE_ADMIN;

  const hasCourseAdmin = courseUsersForUser.some((cu) => cu.role === CourseRole.COURSE_ADMIN);
  if (hasCourseAdmin) return MaxRole.COURSE_ADMIN;

  const hasInstructor = sectionUsersForUser.some((cu) => cu.role === SectionRole.INSTRUCTOR);
  if (hasInstructor) return MaxRole.INSTRUCTOR;

  const hasSectionAssistant = sectionUsersForUser.some(
    (cu) => cu.role === SectionRole.SECTION_ASSISTANT,
  );
  if (hasSectionAssistant) return MaxRole.SECTION_ASSISTANT;

  return MaxRole.STUDENT;
}

type SortField = "name" | "email" | "maxRole" | "external_id" | "invite_status";

interface AdminUsersListProps {
  onShowBulkImportModal: (importType: BulkImportType) => void;
}

export const AdminUsersList: FC<AdminUsersListProps> = ({ onShowBulkImportModal }) => {
  const {
    value: addUserModalIsOpen,
    setTrue: openAddUserModal,
    setFalse: closeAddUserModal,
  } = useBoolean(false);
  // AdminUserOptionsMenu/EditOrgUserModal consume full user profile fields.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const allUsers = useListSelector(userSelectors.selectAll);
  const orgUsers = useListSelector(orgUserSelectors.selectAll, [
    "id",
    "user_id",
    "role",
    "external_user_id",
    "is_disabled",
  ]);
  // Invite-status map keyed by user id; only keyed lookups.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const userInviteStatuses = useObjectSelector(selectUserInviteStatuses);
  const courseUsers = useListSelector(courseUserSelectors.selectAll, ["user_id", "role"]);
  const sectionUsers = useListSelector(selectEnabledSectionUsersByRole, [
    "id",
    "user_id",
    "section_id",
    "role",
  ]);
  const courseGroupingUsers = useListSelector(courseGroupingUserSelectors.selectAll, [
    "user_id",
    "role",
  ]);
  const sections = useListSelector(sectionSelectors.selectAll, ["id", "title"]);
  const currentOrg = useObjectSelector(selectCurrentOrg, ["name"]);
  const { canSeeAllOrgUsers } = useCapabilities();
  const editModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isSmallScreen = !useIsMdOrLarger();

  useEffect(() => {
    storeApi.section_users.list({});
    storeApi.course_users.list({});
    storeApi.course_grouping_users.list({});
  }, []);

  useFetchUserInviteStatuses();

  // OrgUsers and SectionUsers are both links to the User model, and to Org and Section separately,
  // and both have a "role" string attached.
  // In this view we'd like to show a list of all users that have an OrgUser, their role, and the
  // amount of SectionUsers that they have.

  const entries: AdminUserEntry[] = orgUsers
    .flatMap((orgUser) => {
      const user = allUsers.find((u) => u.id === orgUser.user_id);
      if (!user || orgUser.role === OrgRole.RECORDING_AGENT) {
        return [];
      }

      if (!canSeeAllOrgUsers && userHasGlobalAuthority(user)) {
        return [];
      }

      const sectionUsersForUser = sectionUsers.filter((su) => su.user_id === orgUser.user_id);
      const courseUsersForUser = courseUsers.filter((cu) => cu.user_id === orgUser.user_id);
      const courseGroupingUsersForUser = courseGroupingUsers.filter(
        (cgu) => cgu.user_id === orgUser.user_id,
      );

      const tooltipSectionRoleTable = sectionUsersForUser.map((cu) => {
        const section = sections.find((s) => s.id === cu.section_id);
        const role = getSectionResourceRole(cu.role);

        return (
          <div key={cu.id}>
            <strong>{section?.title}</strong>: {t(`roles.section_${role}`)}
          </div>
        );
      });

      const maxRole = getMaxRole(
        orgUser,
        user,
        sectionUsersForUser,
        courseUsersForUser,
        courseGroupingUsersForUser,
      );

      return [
        {
          user,
          orgUser,
          tooltipSectionRoleTable,
          inviteStatus: userInviteStatuses[user.id] || InviteStatus.NOT_SENT,
          maxRole,
          isCourseAdmin: courseUsersForUser.length > 0 || courseGroupingUsersForUser.length > 0,
        },
      ];
    })
    .toSorted((a, b) => {
      const resolveSortRole = (entry: AdminUserEntry): OrgRole | CourseRole | null => {
        if (entry.orgUser.role == null) {
          return entry.isCourseAdmin ? CourseRole.COURSE_ADMIN : null;
        }
        return entry.orgUser.role;
      };
      const roleDiff =
        getOrgRoleSortOrder(resolveSortRole(a)) - getOrgRoleSortOrder(resolveSortRole(b));
      return roleDiff !== 0 ? roleDiff : sortByFirstName(a.user, b.user);
    });

  const canSeeExternalUserIds = entries.some((e) => e.orgUser.external_user_id);

  const getOrgUserSortValue = useCallback(
    (entry: AdminUserEntry, field: SortField): number | string | null => {
      switch (field) {
        case "name":
          return `${(entry.user.first_name ?? "").toLowerCase()} ${(entry.user.last_name ?? "").toLowerCase()}`.trim();
        case "email":
          return entry.user.email ?? "";
        case "maxRole":
          return MAX_ROLE_SORT_ORDER[entry.maxRole];
        case "external_id":
          return entry.orgUser.external_user_id ?? "";
        case "invite_status":
          return entry.inviteStatus;
        default:
          return null;
      }
    },
    [],
  );

  const onDownloadCSV = () => {
    if (!currentOrg) {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
      return;
    }
    if (entries.length === 0) {
      toastErrorMessage(t("section_enrollment_report.no_data"));
      return;
    }
    try {
      const { csvData, filename } = generateOrgUsersCSV(
        entries.map((entry) => ({
          first_name: entry.user.first_name,
          last_name: entry.user.last_name,
          email: entry.user.email,
          maxRole: entry.maxRole,
          external_user_id: entry.orgUser.external_user_id,
          invite_status: entry.inviteStatus,
        })),
        currentOrg.name,
        canSeeExternalUserIds,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
    }
  };

  return (
    <div className="h-full overflow-y-auto pb-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="mb-0 hidden md:block">{t("user_options_menu.org_sidebar_title")}</h3>
        <div className={clsx("flex gap-2", isSmallScreen && "w-full")}>
          <SquareButton
            aria-label={t("admin.add_user.add_user")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Add new"
            kind="tertiary"
            size="xs"
            onClick={openAddUserModal}
          >
            {t("admin.add_user.add_user")}
          </SquareButton>

          <BulkImportMenu disabled={!editModeEnabled} onSelectImportType={onShowBulkImportModal} />

          <SquareButton
            aria-label={t("user_options_menu.download_csv")}
            className="flex-1 md:flex-none"
            disabled={entries.length === 0}
            eventName="Org Users - Download CSV"
            kind="tertiary"
            size="xs"
            onClick={onDownloadCSV}
          >
            <IconText
              iconStart={<ArrowDownToLineIcon />}
              text={t("user_options_menu.download_csv")}
            />
          </SquareButton>
        </div>
      </div>

      <ScrollableTableContainer className="mb-0">
        <SortableTableWrapper<AdminUserEntry, SortField>
          data={entries}
          getSortValue={getOrgUserSortValue}
        >
          <SortableTable>
            <thead>
              <tr>
                <SortableHeader field="name" label={t("user_options_menu.table_header_name")} />
                <SortableHeader field="email" label={t("user_options_menu.table_header_email")} />
                <SortableHeader
                  field="maxRole"
                  label={t("user_options_menu.table_header_max_role")}
                />
                {canSeeExternalUserIds && (
                  <SortableHeader
                    field="external_id"
                    label={t("user_options_menu.table_header_external_id")}
                  />
                )}
                <SortableHeader
                  field="invite_status"
                  label={t("user_options_menu.table_header_invite_status")}
                />
              </tr>
            </thead>

            <SortableBody<AdminUserEntry>>
              {(sortedData) =>
                sortedData.map((entry) => (
                  <AdminUserRow
                    entry={entry}
                    key={entry.user.id}
                    showExternalId={canSeeExternalUserIds}
                  />
                ))
              }
            </SortableBody>
          </SortableTable>
        </SortableTableWrapper>
      </ScrollableTableContainer>
      <AddUserModal open={addUserModalIsOpen} onClose={closeAddUserModal} />
    </div>
  );
};

type AdminUserRowProps = {
  entry: AdminUserEntry;
  showExternalId?: boolean;
};

export const AdminUserRow: FC<AdminUserRowProps> = ({ entry, showExternalId = false }) => {
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean(false);
  const { user, orgUser } = entry;
  const displayRole = getDisplayOrgRole(user, orgUser, entry.isCourseAdmin);

  return (
    <TR className="items-center justify-between">
      <TD className={clsx(orgUser.is_disabled && "italic text-black-tint-70")}>
        <div className="flex items-center gap-1">
          <OrgRoleIconWithTooltip extraContent={entry.tooltipSectionRoleTable} role={displayRole} />

          <span className="max-w-m truncate" title={getUserFullName(user)}>
            {getUserFullName(user)}
          </span>
        </div>
      </TD>

      <TD className="body-xs relative">{user.email}</TD>

      <TD className="body-xs relative">{t(`user_options_menu.max_role.${entry.maxRole}`)}</TD>

      {showExternalId && <TD className="body-xs relative">{orgUser.external_user_id || "-"}</TD>}

      <TD className="body-xs flex items-center justify-between">
        <span>{t(`invite_status.${entry.inviteStatus}`)}</span>
        <AdminUserOptionsMenu
          inviteStatus={entry.inviteStatus}
          orgUser={orgUser}
          user={user}
          onEdit={openModal}
        />

        <EditOrgUserModal open={isModalOpen} orgUser={orgUser} user={user} onClose={closeModal} />
      </TD>
    </TR>
  );
};
